import constants from "#src/config/constants";
import {
  ChangeEventHandler,
  FocusEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { IFormCoreInputProps } from "../types";

const defaultInvalid = { required: false, pattern: false, maxLength: false };

interface IUseFormCoreInputProps {
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  validation?: {
    required?: boolean;
    pattern?: RegExp;
    maxLength?: number;
  };
  onChangeExternal?: (value: string) => void;
}

export const useFormCoreInput = ({
  defaultValue,
  placeholder,
  validation,
  disabled,
  onChangeExternal,
}: IUseFormCoreInputProps) => {
  const { t } = useTranslation("common", { keyPrefix: "input" });

  // --------------- STATES ---------------

  const [value, setValue] = useState<string>(defaultValue ?? "");

  const [touched, setTouched] = useState<boolean>(false);

  const [isInvalid, setInvalid] = useState<{
    required: boolean;
    pattern: boolean;
    maxLength: boolean;
  }>(defaultInvalid);

  const [externalError, setExternalError] = useState<string>("");

  // --------------- COMPUTED ---------------

  const wasChanged = useMemo(
    () => typeof defaultValue === "string" && value !== defaultValue,
    [value, defaultValue]
  );

  const errorText = useMemo(() => {
    if (touched) {
      if (isInvalid.required) return t("error.required");
      if (isInvalid.pattern) return t("error.invalid");
      if (isInvalid.maxLength) return t("error.maxLength");
    }

    if (externalError) return externalError;

    return null;
  }, [touched, isInvalid, externalError, t]);

  // --------------- METHODS ---------------

  const reset = () => {
    if (defaultValue) {
      setValue(defaultValue);
      if (onChangeExternal) {
        onChangeExternal(defaultValue);
      }
    } else {
      setValue("");
      if (onChangeExternal) {
        onChangeExternal("");
      }
    }
    setTouched(false);
    setInvalid(defaultInvalid);
    setExternalError("");
  };

  const onValidate = useDebouncedCallback((value: string) => {
    if (!touched) return;

    setExternalError("");

    if (validation?.required) {
      if (value.length === 0) {
        setInvalid((prev) => ({ ...prev, required: true }));
        return;
      } else {
        setInvalid((prev) => ({ ...prev, required: false }));
      }
    }

    if (validation?.pattern) {
      if (!validation.pattern.test(value)) {
        setInvalid((prev) => ({ ...prev, pattern: true }));
        return;
      } else {
        setInvalid((prev) => ({ ...prev, pattern: false }));
      }
    }

    if (validation?.maxLength) {
      if (value.length > validation.maxLength) {
        setInvalid((prev) => ({ ...prev, maxLength: true }));
        return;
      } else {
        setInvalid((prev) => ({ ...prev, maxLength: false }));
      }
    }

    setInvalid(defaultInvalid);
    return;
  }, constants.DEBOUNCE_TIME);

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      setValue(e.target.value);
      onValidate(e.target.value);
      if (onChangeExternal) onChangeExternal(e.target.value);
    },
    [onValidate, onChangeExternal]
  );

  const onFocus = useCallback<FocusEventHandler<HTMLInputElement>>(() => {
    setTouched(true);
  }, []);

  const onBlur = useCallback<FocusEventHandler<HTMLInputElement>>(() => {
    onValidate(value);
  }, [onValidate, value]);

  const renderProps = useMemo(
    () =>
      ({
        value,
        placeholder,
        disabled,
        onChange,
        onFocus,
        onBlur,
      }) satisfies IFormCoreInputProps,
    [value, placeholder, disabled, onChange, onFocus, onBlur]
  );

  // --------------- EFFECTS ---------------

  useEffect(() => {
    if (defaultValue) setValue(defaultValue);
  }, [defaultValue]);

  return { value, reset, setExternalError, errorText, wasChanged, renderProps };
};
